import React from "react";

export interface TabContextValue {
  value: unknown;
  // Prefixo único para gerar ids de tab/painel (acessibilidade), igual ao MUI lab.
  idPrefix: string;
}

export const TabContextInstance = React.createContext<TabContextValue | null>(null);

// Hook interno usado por TabPanel para ler o contexto e falhar de forma clara
// quando usado fora de um TabContext.
export const useTabContext = (): TabContextValue => {
  const context = React.useContext(TabContextInstance);

  if (context === null) {
    throw new TypeError("No TabContext provided");
  }

  return context;
};

export const getPanelId = (context: TabContextValue, value: unknown): string =>
  `${context.idPrefix}-P-${String(value)}`;

export const getTabId = (context: TabContextValue, value: unknown): string =>
  `${context.idPrefix}-T-${String(value)}`;

export interface TabContextProps<T = unknown> {
  value: T;
  children?: React.ReactNode;
}

let globalTabContextCount = 0;

const TabContext = <T = unknown>(props: TabContextProps<T>): JSX.Element => {
  const { value, children } = props;
  // Gera um prefixo estável por instância para os ids de acessibilidade.
  const [idPrefix] = React.useState(() => {
    globalTabContextCount += 1;

    return `tab-context-${globalTabContextCount}`;
  });

  const context = React.useMemo<TabContextValue>(() => ({ value, idPrefix }), [value, idPrefix]);

  return (
    <TabContextInstance.Provider value={context}>
      {children}
    </TabContextInstance.Provider>
  );
};

export default TabContext;
